import CommentsInput from 'components/Comments/CommentsInput'
import CommandCenterTasks from 'components/Creative/CommandCenterTasks'
import KickoffCall from 'components/DataGrid/components/KickoffCall'
import IntegrationChecklistTable from 'components/DataGrid/components/IntegrationChecklistTable'
import ReviewDeal from 'components/DealApproval/ReviewDeal'
import RenderCampaign from 'components/StrategyCampaigns/RenderCampaign'
import OnboardingTasks from './OnboardingTasks'
import { OrganizationTableItem, TabItem, Checklist } from 'types'

type MonthKeyFromISO = (iso: string, tz?: string) => string
type CompletedCounts = {
  onboardingprogress: number
  completeseoform: number
  fbpage: number
  tiktok: number
  linkedin: number
  microsoft: number
  spotify: number
  youtube: number
  ga4: number
  gtm: number
  mia: number
}
// Matches OnboardingTasks `checklistProgress` exactly
type OnboardingChecklistProgress = {
  gtm: number
  ga4: number
  fbpage: number
  completeseoform: number
}

// Matches OnboardingTasks `progressOverride` exactly
type OnboardingProgressSummary = {
  total: number
  done: number
  pct: number
  byRole: Record<string, { done: number; total: number }>
}

// (Optional) define if you want lightweight shapes for lists you pass through
type CommandCenterTask = any

type BuildNcoArgs = {
  deal: OrganizationTableItem | null
  organization: OrganizationTableItem | null

  // layout
  expanded?: boolean

  // month filtering (kept for parity—even if unused today)
  monthKey?: string
  monthKeyFromISO?: MonthKeyFromISO
  tz?: string

  // ONBOARDING TAB – strongly typed to match your component
  onboarding: {
    tasklists: Checklist[] // `checklists`
    checklistProgress: OnboardingChecklistProgress
    onChange: (fieldName: any, newValue: any) => Promise<void>
    currentChecklist?: any | null // `currentChecklistOverride`
    seededRows?: any[] | null // `seededRowsOverride`
    completedSet?: Set<string> | null // `completedSetOverride`
    progress?: OnboardingProgressSummary | null // `progressOverride`
    expanded?: boolean
    completedCounts?: CompletedCounts
  }
  reviewDeal?: {
    moduleLocation?: string
  }
  // COMMAND CENTER TASKS
  ncoTasks: CommandCenterTask[]

  // COMMENTS TAB
  comments: {
    taskSKs: string[]
    context: string
    expanded: boolean
    contextFilters: string[]
    checklistItems: object[] | []
  }

  // KICKOFF CALL – uses the real KickoffCall prop type
  kickoff: {
    onCreateDoc: (data: Record<string, any>) => void
    isCreating?: boolean
  }

  // INTEGRATION CHECKLIST
  integration: {
    fields: any
    setFields: (v: any) => void
    checklist: any[] // IntegrationChecklistTable expects `any[]`
    onMirrorChecklists?: (producer: (prev: any[] | undefined) => any[]) => void
    onSaved?: () => void
  }

  // SEO CHECKLIST
  seoChecklist: any[]
}

// Stable empty object to prevent unnecessary re-renders
const EMPTY_FORM_DATA: Record<string, any> = {}

export const buildNcoTabsConfig = ({
  deal,
  organization,
  expanded,
  onboarding,
  ncoTasks,
  comments,
  kickoff,
  integration,
  seoChecklist,
}: BuildNcoArgs): TabItem[] => {
  return [
    {
      label: 'main',
      display: 'Onboarding Tasks',
      Component: OnboardingTasks,
      props: {
        deal,
        organization,
        checklists: onboarding.tasklists,
        checklistType: 'Onboarding',
        checklistProgress: onboarding.checklistProgress,
        expanded: onboarding.expanded ?? expanded ?? false,
        onChange: onboarding.onChange,
        currentChecklistOverride: onboarding.currentChecklist ?? null,
        seededRowsOverride: onboarding.seededRows ?? null,
        completedSetOverride: onboarding.completedSet ?? null,
        progressOverride: onboarding.progress ?? null,
      },
      needsEditing: false,
    },
    {
      label: 'tasks',
      display: 'Command Center Tasks',
      Component: CommandCenterTasks,
      props: { tasks: ncoTasks, expanded },
      needsEditing: true,
      saveKind: 'all',
    },
    {
      label: 'strategy',
      display: 'Render Campaign',
      Component: RenderCampaign,

      needsEditing: false,
    },
    {
      label: 'review',
      display: 'Review Deal',
      Component: ReviewDeal,
      props: { moduleLocation: 'NcoGridDrawer', isHeaderExpanded: !!expanded },
      needsEditing: false,
    },
    {
      label: 'comments',
      display: 'Comments',
      Component: CommentsInput,
      props: {
        taskSKs: comments.taskSKs,
        contextFilters: comments.contextFilters,
        expanded: !!expanded,
        context: comments.context,
        checklistItems: comments.checklistItems,
      },
      needsEditing: false,
    },
    {
      label: 'kickoff',
      display: 'Kickoff Call',
      Component: KickoffCall,
      props: {
        organization,
        deal,
        onCreateDoc: kickoff.onCreateDoc, // <-- pass ref, no inline lambda
        expanded,
        isCreating: kickoff.isCreating,
      },
      needsEditing: false,
    },
    {
      label: 'integration',
      display: 'Integration',
      Component: IntegrationChecklistTable,
      props: {
        deal,
        organization,
        formData: integration.fields,
        setFormData: integration.setFields,
        checklistData: integration.checklist ?? [],
        expanded: !!expanded,
        onMirrorChecklists: integration.onMirrorChecklists, // pass ref
        onSaved: integration.onSaved, // pass ref
        type: 'integration',
      },
      needsEditing: false,
    },
    {
      label: 'seo',
      display: 'SEO',
      Component: IntegrationChecklistTable,
      props: {
        deal,
        organization,
        formData: EMPTY_FORM_DATA,
        setFormData: undefined, // avoid new no-op fn each time
        checklistData: seoChecklist ?? [],
        expanded: !!expanded,
        onMirrorChecklists: integration.onMirrorChecklists,
        onSaved: integration.onSaved,
        type: 'seo',
      },
      needsEditing: true,
      saveKind: 'checklist',
    },
  ]
}
